import { useEffect, useState } from 'react';
import api from '../services/api';
import { useUser } from '../contexts/UserContext';
import { Agency, Group } from '../types';

function Groups() {
  const { user: currentUser } = useUser();
  const [groups, setGroups] = useState<Group[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createFormData, setCreateFormData] = useState({ name: '', code: '', agencyId: '' });
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [editFormData, setEditFormData] = useState({ name: '', code: '', agencyId: '', isActive: true });
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState<Group | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    void fetchData();
  }, [currentUser]);

  const canManageGroups = currentUser?.roles?.some(
    (role) => role.id === 'admin-role-id' || role.id === 'agency-admin-role-id',
  );

  const getAgencyName = (agencyId: string) => {
    const agency = agencies.find((a) => a.id === agencyId);
    return agency ? agency.name : agencyId;
  };

  const fetchData = async () => {
    if (!currentUser) return;
    setLoading(true);
    setError(null);
    try {
      const [groupsResponse, agenciesResponse] = await Promise.all([
        api.get('/groups'),
        api.get('/agencies'),
      ]);
      setGroups(groupsResponse.data);
      setAgencies(agenciesResponse.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load groups or agencies');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setCreateFormData({ name: '', code: '', agencyId: agencies[0]?.id || '' });
    setCreateError(null);
    setIsCreateModalOpen(true);
  };

  const handleCreateGroup = async () => {
    setIsCreating(true);
    setCreateError(null);
    try {
      const response = await api.post('/groups', {
        id: `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: createFormData.name.trim(),
        code: createFormData.code.trim(),
        agencyId: createFormData.agencyId,
        isActive: true,
      });
      setGroups((prev) => [response.data, ...prev]);
      setIsCreateModalOpen(false);
    } catch (err: any) {
      setCreateError(err.response?.data?.error || err.response?.data?.message || 'Failed to create group');
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenEdit = (group: Group) => {
    setEditingGroup(group);
    setEditFormData({ name: group.name, code: group.code, agencyId: group.agencyId, isActive: group.isActive });
    setUpdateError(null);
    setIsEditModalOpen(true);
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup) return;
    setIsUpdating(true);
    setUpdateError(null);
    try {
      const response = await api.put(`/groups/${editingGroup.id}`, {
        name: editFormData.name.trim(),
        code: editFormData.code.trim(),
        agencyId: editFormData.agencyId,
        isActive: editFormData.isActive,
      });
      setGroups((prev) => prev.map((g) => (g.id === editingGroup.id ? response.data : g)));
      setIsEditModalOpen(false);
    } catch (err: any) {
      setUpdateError(err.response?.data?.error || err.response?.data?.message || 'Failed to update group');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleOpenDelete = (group: Group) => {
    setDeletingGroup(group);
    setDeleteError(null);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteGroup = async () => {
    if (!deletingGroup) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await api.delete(`/groups/${deletingGroup.id}`);
      setGroups((prev) => prev.filter((g) => g.id !== deletingGroup.id));
      setIsDeleteModalOpen(false);
    } catch (err: any) {
      setDeleteError(err.response?.data?.error || err.response?.data?.message || 'Failed to delete group');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div>
        <h1>Group Management</h1>
        <div style={{ marginTop: '20px', color: 'var(--secondary-color)' }}>Loading groups...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h1>Group Management</h1>
        <div style={{ marginTop: '20px', color: 'var(--primary-color)' }}>Error: {error}</div>
      </div>
    );
  }

  return (
    <div>
      <h1>Group Management</h1>

      {canManageGroups && (
        <div style={{ marginTop: '20px' }}>
          <button onClick={handleOpenCreate} className="btn-primary" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span className="material-symbols-outlined small">add</span>
            Add New Group
          </button>
        </div>
      )}

      <div style={{ marginTop: '30px' }}>
        <h2>Groups List</h2>
        {groups.length === 0 ? (
          <p style={{ color: 'var(--secondary-color)' }}>No groups found.</p>
        ) : (
          <div style={{ backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--background-color)', borderBottom: '1px solid #e0e0e0' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left' }}>Code</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left' }}>Agency</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left' }}>Created</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left' }}>Updated</th>
                  {canManageGroups && <th style={{ padding: '12px 16px', textAlign: 'left' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <tr key={group.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px 16px' }}>{group.name}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: '#e9ecef', fontFamily: 'monospace' }}>{group.code}</span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>{getAgencyName(group.agencyId)}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          backgroundColor: group.isActive ? '#d4edda' : '#f8d7da',
                          color: group.isActive ? '#155724' : '#721c24',
                        }}
                      >
                        {group.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#6c757d' }}>{new Date(group.createdAt).toLocaleDateString()}</td>
                    <td style={{ padding: '12px 16px', color: '#6c757d' }}>{new Date(group.updatedAt).toLocaleDateString()}</td>
                    {canManageGroups && (
                      <td style={{ padding: '12px 16px', display: 'flex', gap: '8px' }}>
                        <button onClick={() => handleOpenEdit(group)} className="btn-outline">
                          Edit
                        </button>
                        <button onClick={() => handleOpenDelete(group)} className="btn-secondary">
                          Delete
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isCreateModalOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', width: '90%', maxWidth: '500px' }}>
            <h2 style={{ marginTop: 0 }}>Create New Group</h2>
            {createError && <div style={{ marginBottom: '12px', color: '#721c24' }}>{createError}</div>}
            <input
              type="text"
              placeholder="Group name"
              value={createFormData.name}
              onChange={(e) => setCreateFormData((prev) => ({ ...prev, name: e.target.value }))}
              style={{ width: '100%', marginBottom: '12px', padding: '10px', boxSizing: 'border-box' }}
            />
            <input
              type="text"
              placeholder="Group code"
              value={createFormData.code}
              onChange={(e) => setCreateFormData((prev) => ({ ...prev, code: e.target.value }))}
              style={{ width: '100%', marginBottom: '12px', padding: '10px', boxSizing: 'border-box' }}
            />
            <select
              value={createFormData.agencyId}
              onChange={(e) => setCreateFormData((prev) => ({ ...prev, agencyId: e.target.value }))}
              style={{ width: '100%', marginBottom: '18px', padding: '10px', boxSizing: 'border-box' }}
            >
              <option value="">Select an agency</option>
              {agencies.map((agency) => (
                <option key={agency.id} value={agency.id}>
                  {agency.name} ({agency.code})
                </option>
              ))}
            </select>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setIsCreateModalOpen(false)} className="btn-outline" disabled={isCreating}>
                Cancel
              </button>
              <button
                onClick={handleCreateGroup}
                className="btn-primary"
                disabled={isCreating || !createFormData.name.trim() || !createFormData.code.trim() || !createFormData.agencyId}
              >
                {isCreating ? 'Creating...' : 'Create Group'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isEditModalOpen && editingGroup && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', width: '90%', maxWidth: '500px' }}>
            <h2 style={{ marginTop: 0 }}>Edit Group</h2>
            {updateError && <div style={{ marginBottom: '12px', color: '#721c24' }}>{updateError}</div>}
            <input
              type="text"
              placeholder="Group name"
              value={editFormData.name}
              onChange={(e) => setEditFormData((prev) => ({ ...prev, name: e.target.value }))}
              style={{ width: '100%', marginBottom: '12px', padding: '10px', boxSizing: 'border-box' }}
            />
            <input
              type="text"
              placeholder="Group code"
              value={editFormData.code}
              onChange={(e) => setEditFormData((prev) => ({ ...prev, code: e.target.value }))}
              style={{ width: '100%', marginBottom: '12px', padding: '10px', boxSizing: 'border-box' }}
            />
            <select
              value={editFormData.agencyId}
              onChange={(e) => setEditFormData((prev) => ({ ...prev, agencyId: e.target.value }))}
              style={{ width: '100%', marginBottom: '12px', padding: '10px', boxSizing: 'border-box' }}
            >
              <option value="">Select an agency</option>
              {agencies.map((agency) => (
                <option key={agency.id} value={agency.id}>
                  {agency.name} ({agency.code})
                </option>
              ))}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '18px' }}>
              <input
                type="checkbox"
                checked={editFormData.isActive}
                onChange={(e) => setEditFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
              />
              Active Group
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setIsEditModalOpen(false)} className="btn-outline" disabled={isUpdating}>
                Cancel
              </button>
              <button
                onClick={handleUpdateGroup}
                className="btn-primary"
                disabled={isUpdating || !editFormData.name.trim() || !editFormData.code.trim() || !editFormData.agencyId}
              >
                {isUpdating ? 'Updating...' : 'Update Group'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isDeleteModalOpen && deletingGroup && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', width: '90%', maxWidth: '500px' }}>
            <h2 style={{ marginTop: 0, color: '#dc3545' }}>Confirm Delete</h2>
            {deleteError && <div style={{ marginBottom: '12px', color: '#721c24' }}>{deleteError}</div>}
            <p>Delete group <strong>{deletingGroup.name}</strong>? This action cannot be undone.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setIsDeleteModalOpen(false)} className="btn-outline" disabled={isDeleting}>
                Cancel
              </button>
              <button onClick={handleDeleteGroup} className="btn-secondary" disabled={isDeleting}>
                {isDeleting ? 'Deleting...' : 'Delete Group'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Groups;
